import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as metricsApi from '@/api/metrics.api';

/**
 * Query keys for metrics
 */
export const metricsKeys = {
  all: ['metrics'] as const,
  user: (userId: string, period?: string, days?: number) =>
    [...metricsKeys.all, 'user', userId, period, days] as const,
  team: (projectId: string, period?: string, days?: number) =>
    [...metricsKeys.all, 'team', projectId, period, days] as const,
  leaderboard: (days?: number, projectId?: string, limit?: number) =>
    [...metricsKeys.all, 'leaderboard', days, projectId, limit] as const,
  overview: (days: number) => [...metricsKeys.all, 'overview', days] as const,
};

/**
 * Fetch user metrics
 */
export function useUserMetrics(
  userId: string,
  params?: { period?: string; days?: number }
) {
  return useQuery({
    queryKey: metricsKeys.user(userId, params?.period, params?.days),
    queryFn: () => metricsApi.getUserMetrics(userId, params),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch team metrics
 */
export function useTeamMetrics(
  projectId: string,
  params?: { period?: string; days?: number }
) {
  return useQuery({
    queryKey: metricsKeys.team(projectId, params?.period, params?.days),
    queryFn: () => metricsApi.getTeamMetrics(projectId, params),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch leaderboard
 */
export function useLeaderboard(params?: {
  days?: number;
  projectId?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: metricsKeys.leaderboard(params?.days, params?.projectId, params?.limit),
    queryFn: () => metricsApi.getLeaderboard(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch metrics overview
 */
export function useMetricsOverview(days = 30) {
  return useQuery({
    queryKey: metricsKeys.overview(days),
    queryFn: () => metricsApi.getMetricsOverview(days),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Calculate metrics mutation
 */
export function useCalculateMetrics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: metricsApi.calculateMetrics,
    onSuccess: () => {
      // Invalidate all metrics queries
      queryClient.invalidateQueries({ queryKey: metricsKeys.all });
    },
  });
}
